/**
 * §7.9 Lab — merged Now/Forecast wrapper (CD R11 #2 — kill list).
 *
 * Per CD R11 finding #2: Lab Capacity (§7.9) and Lab Bottleneck (§7.31)
 * were two top-level peers of the same noun on different time-horizons.
 * R9 rule — same noun, two time-horizons → that's a MODE, not two
 * surfaces. Merged here as a Now / Forecast tab pair; surface count
 * drops 34 → 33. Mirrors the EnsembleForecast Forecast/Validation
 * pattern shipped on §7.4b.
 *
 *   Now tab     → §7.9 — per-region PCR throughput vs projected demand
 *                 snapshot (`LabCapacityBody`)
 *   Forecast    → §7.31 — day-by-day saturation projection + workaround
 *                 comparison (`LabBottleneckForecaster`)
 *
 * Sub-section labels are preserved on the inner bodies; the registry
 * entry's `section` field stays `§7.9` (the wrapped surface keeps the
 * Now sub-section as its primary nav identity).
 */

import { useMemo, useState } from 'react';
import { findPathogen, listPathogens } from '@biomax/plugin-pathogen-database';
import { listRegions } from '@biomax/plugin-region-database';
import { simulateSeird } from '@biomax/plugin-epidemiology';
import { Sparkline } from '../sparkline';
import { auditCapture } from '../audit/capture';
import { LabBottleneckForecaster } from './LabBottleneckForecaster';

type LabTab = 'now' | 'forecast';

/**
 * Top-level Lab surface. Tab pair: "Now" (the §7.9 per-region snapshot)
 * + "Forecast" (the §7.31 day-by-day saturation projection). Tab switch
 * emits a `surface.view` audit event so the audit panel can reconstruct
 * which sub-mode the operator was in at decision-time.
 */
export function LabCapacity() {
  const [tab, setTab] = useState<LabTab>('now');

  const switchTab = (next: LabTab) => {
    if (next === tab) return;
    const subSection = next === 'now' ? '§7.9' : '§7.31';
    auditCapture.emit({
      event: {
        type: 'surface.view',
        surface: subSection,
        subjectIds: [],
        payload: { labTab: next, from: tab },
      },
    });
    setTab(next);
  };

  return (
    <>
      <div role="tablist" aria-label="Lab mode" style={{
        display: 'flex', gap: 0,
        marginBottom: 'var(--bm-space-3)',
        borderBottom: '1px solid var(--bm-border)',
      }}>
        {([
          { k: 'now' as const,      label: 'Now',      section: '§7.9' },
          { k: 'forecast' as const, label: 'Forecast', section: '§7.31' },
        ]).map(t => {
          const on = tab === t.k;
          return (
            <button
              key={t.k}
              role="tab"
              aria-selected={on}
              onClick={() => switchTab(t.k)}
              style={{
                padding: '10px 16px',
                background: on ? 'var(--bm-bg)' : 'transparent',
                border: 'none',
                borderBottom: `2px solid ${on ? 'var(--bm-accent, #6ea3ff)' : 'transparent'}`,
                fontFamily: 'var(--bm-font-mono)', fontSize: 11.5, fontWeight: 600,
                color: on ? 'var(--bm-text)' : 'var(--bm-text-subtle)',
                cursor: 'pointer',
              }}
            >
              <span style={{ opacity: 0.7, marginRight: 6 }}>{t.section}</span>
              {t.label}
            </button>
          );
        })}
      </div>
      {tab === 'now'      && <LabCapacityBody />}
      {tab === 'forecast' && <LabBottleneckForecaster />}
    </>
  );
}

function LabCapacityBody() {
  const pathogens = listPathogens();
  const regions = listRegions();

  const [pathogenId, setPathogenId] = useState<string>('sars-cov-2-wildtype');
  const [testSeekingFraction, setTestSeekingFraction] = useState<number>(60); // % of infected who seek a test

  const view = useMemo(() => {
    const pathogen = findPathogen(pathogenId);
    if (!pathogen) return null;

    const gamma = 1 / pathogen.infectiousPeriodDays.value;
    const sigma = 1 / pathogen.latentPeriodDays.value;
    const beta = pathogen.R0.value * gamma;
    const ifr = pathogen.ifrByAgeBand[Math.floor(pathogen.ifrByAgeBand.length / 2)]?.ifr ?? 0.005;

    const perRegion = regions
      .filter(r => r.labCapacity?.pcrPerDayPer100k !== undefined)
      .map(r => {
        const sim = simulateSeird({
          N: r.population.total, I0: 100, beta, gamma, sigma, ifr,
          durationDays: 120, dtDays: 1,
        });
        const S = sim.compartments.S!;
        const dailyIncidence: number[] = [];
        for (let i = 1; i < S.length; i++) {
          dailyIncidence.push(Math.max(0, S[i - 1]! - S[i]!));
        }
        const peakIncidence = Math.max(...dailyIncidence);
        const peakDay = dailyIncidence.indexOf(peakIncidence);
        const testDemandPeak = peakIncidence * (testSeekingFraction / 100);
        const pcrCapacity = (r.labCapacity!.pcrPerDayPer100k ?? 0) * (r.population.total / 100_000);
        const utilizationPeak = pcrCapacity > 0 ? testDemandPeak / pcrCapacity : Infinity;
        const seqPerWeek = r.labCapacity!.sequencingPerWeek ?? 0;
        const seqPerDay = seqPerWeek / 7;
        const sequencingShare = peakIncidence > 0 ? seqPerDay / peakIncidence : 0;
        return {
          region: r, dailyIncidence, peakIncidence, peakDay,
          testDemandPeak, pcrCapacity, utilizationPeak, seqPerDay, sequencingShare,
        };
      })
      .sort((a, b) => b.utilizationPeak - a.utilizationPeak);

    return { perRegion };
  }, [pathogenId, testSeekingFraction, regions]);

  if (!view) return <div>Loading…</div>;

  const usaRow = view.perRegion.find(p => p.region.id === 'usa');

  return (
    <div className="surface">
      <div className="surface__header">
        <span className="surface__sec">§7.9</span>
        <h1 className="surface__title">Lab Capacity</h1>
      </div>
      <div className="surface__sub">
        Per-region PCR throughput from region catalog (OECD + WHO) vs simulated diagnostic
        demand · {view.perRegion.length} regions with labCapacity data
      </div>

      <div className="panel">
        <div className="panel__head"><span className="panel__title">Scenario</span></div>
        <div className="simctl">
          <label className="simctl__field">
            <span className="simctl__label">Pathogen</span>
            <select value={pathogenId} onChange={e => setPathogenId(e.target.value)}>
              {pathogens.map(p => <option key={p.id} value={p.id}>{p.names.common}</option>)}
            </select>
          </label>
          <label className="simctl__field">
            <span className="simctl__label">Test-seeking fraction · {testSeekingFraction}%</span>
            <input type="range" min="20" max="100" step="5" value={testSeekingFraction}
              onChange={e => setTestSeekingFraction(parseInt(e.target.value, 10))} />
          </label>
        </div>
      </div>

      {usaRow && (
        <div className="kpis">
          <div className="kpi">
            <div className="kpi__label">USA peak demand</div>
            <div className="kpi__value kpi__value--signal">{Math.round(usaRow.testDemandPeak / 1e3).toLocaleString()}k</div>
            <div className="kpi__sub">tests / day (day {usaRow.peakDay})</div>
          </div>
          <div className="kpi">
            <div className="kpi__label">USA PCR capacity</div>
            <div className="kpi__value">{Math.round(usaRow.pcrCapacity / 1e3).toLocaleString()}k</div>
            <div className="kpi__sub">tests / day (sustained)</div>
          </div>
          <div className="kpi">
            <div className="kpi__label">USA peak utilization</div>
            <div className={`kpi__value ${usaRow.utilizationPeak > 1 ? 'kpi__value--danger' : 'kpi__value--accent'}`}>
              {(usaRow.utilizationPeak * 100).toFixed(0)}%
            </div>
            <div className="kpi__sub">{usaRow.utilizationPeak > 1 ? 'OVER capacity' : 'within capacity'}</div>
          </div>
          <div className="kpi">
            <div className="kpi__label">USA sequencing share</div>
            <div className="kpi__value">{(usaRow.sequencingShare * 100).toFixed(2)}%</div>
            <div className="kpi__sub">of peak daily incidence</div>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="panel__head">
          <span className="panel__title">Per-region capacity gap (ranked by peak utilization)</span>
          <span className="panel__meta">red = saturated</span>
        </div>
        <table className="dtable">
          <thead>
            <tr>
              <th>Region</th>
              <th className="num">Peak demand / day</th>
              <th className="num">PCR capacity / day</th>
              <th className="num">Peak utilization</th>
              <th className="num">Sequencing / week</th>
            </tr>
          </thead>
          <tbody>
            {view.perRegion.map(r => (
              <tr key={r.region.id}>
                <td>{r.region.names.english}</td>
                <td className="num">{Math.round(r.testDemandPeak).toLocaleString()}</td>
                <td className="num">{Math.round(r.pcrCapacity).toLocaleString()}</td>
                <td className="num" style={{ color: r.utilizationPeak > 1 ? 'var(--bm-danger)' : r.utilizationPeak > 0.7 ? 'var(--bm-signal)' : 'var(--bm-accent)', fontWeight: 600 }}>
                  {(r.utilizationPeak * 100).toFixed(0)}%
                </td>
                <td className="num">{((r.region.labCapacity?.sequencingPerWeek ?? 0) / 1e3).toFixed(0)}k</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {usaRow && (
        <div className="panel">
          <div className="panel__head">
            <span className="panel__title">USA: daily test demand vs PCR capacity</span>
          </div>
          <Sparkline
            values={usaRow.dailyIncidence.map(i => i * (testSeekingFraction / 100))}
            label="Daily test demand"
            stroke="var(--bm-signal)"
          />
          <Sparkline
            values={usaRow.dailyIncidence.map(() => usaRow.pcrCapacity)}
            label={`Sustained PCR capacity (${Math.round(usaRow.pcrCapacity).toLocaleString()}/day)`}
            stroke="var(--bm-accent)"
          />
        </div>
      )}
    </div>
  );
}
